import {
  useAsync,
  useDeepCompareEffect,
  useLocalStorageValue,
  useMountEffect,
} from "@react-hookz/web";
import defaults from "lodash/defaults";
import { useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import type { DeepRequired } from "utility-types";

import { config } from "~/data";
import type { hooksAdvertisementConfigQuery } from "~/queries/__generated__/hooksAdvertisementConfigQuery.graphql";

export const useAdvertisementConfig = () => {
  const { set: setCachedConfig, value: cachedConfig } =
    useLocalStorageValue<RawAdvertisementConfig>("advertisement-config");

  const [resolvedConfig, setResolvedConfig] = useState<AdvertisementConfig>(
    parseConfig(cachedConfig) ?? defaultAdvertisementConfig,
  );
  const environment = useRelayEnvironment();
  const [{ result: remoteConfig }, { execute }] = useAsync(async () => {
    // Set config in drupal
    // Ref: https://${drupal-host}/admin/config/services/app-config
    // Example: https://staging-scmp-d8.product-web.dev-2.scmp.tech/admin/config/services/app-config
    const data = await fetchQuery<hooksAdvertisementConfigQuery>(
      environment,
      graphql`
        query hooksAdvertisementConfigQuery($id: String) {
          appConfig(filter: { entityId: $id }) {
            json
          }
        }
      `,
      {
        id: config.advertisement.configId,
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    if (!data?.appConfig?.json) return;
    return data.appConfig.json as RawAdvertisementConfig;
  });
  useMountEffect(execute);

  useDeepCompareEffect(() => {
    if (!remoteConfig) return;

    setResolvedConfig(parseConfig(remoteConfig) ?? defaultAdvertisementConfig);
    setCachedConfig(remoteConfig);
  }, [remoteConfig, setCachedConfig]);

  return resolvedConfig;
};

export type AdvertisementConfig = {
  autoRefresh: {
    desktopMaximumRefreshLimit: number;
    disabledCountries: string[];
    disabledDirectCampaignLineItemIds: number[];
    minimalScrollAwayDurationMs: number;
    mobileMaximumRefreshLimit: number;
    refreshIntervalMs: number;
  };
  takeoverAd: {
    style: AdvertisementTakeoverAdSectionsConfig;
  };
  viewabilityTracking: {
    enable: boolean;
    eventIntervalMs: number;
    testMode: {
      enable: boolean;
      targetUrlPatterns: string[];
    };
    workerIntervalMs: number;
  };
};

export const defaultAdvertisementConfig: AdvertisementConfig = {
  autoRefresh: {
    desktopMaximumRefreshLimit: 30,
    disabledCountries: [],
    disabledDirectCampaignLineItemIds: [],
    minimalScrollAwayDurationMs: 5000,
    mobileMaximumRefreshLimit: 5,
    refreshIntervalMs: 20_000,
  },
  takeoverAd: {
    style: {
      alwaysEnable: false,
      country: [],
      enableInPageTypes: {
        article: {
          enable: false,
        },
        mainSection: {
          enable: false,
        },
        subSection: {
          enable: false,
        },
      },
      enableTakeoverByDateTime: false,
      endDateTime: 0,
      ipAddress: [],
      startDateTime: 0,
      targetNonSubscribersOnly: false,
      targetSubscribersOnly: false,
    },
  },
  viewabilityTracking: {
    enable: false,
    eventIntervalMs: 10_000,
    testMode: {
      enable: false,
      targetUrlPatterns: [],
    },
    workerIntervalMs: 15_000,
  },
};

type RawAdvertisementTakeoverAdSectionConfig = {
  alwaysEnable?: boolean;
  country?: string[];
  enableInPageTypes?: {
    article?: {
      enable?: boolean;
    };
    mainSection: {
      enable?: boolean;
    };
    subSection: {
      enable?: boolean;
    };
  };
  enableTakeoverByDateTime?: boolean;
  endDateTime?: number;
  ipAddress?: string[];
  startDateTime?: number;
  targetNonSubscribersOnly?: boolean;
  targetSubscribersOnly?: boolean;
};

export type AdvertisementTakeoverAdSectionsConfig =
  DeepRequired<RawAdvertisementTakeoverAdSectionConfig>;

type RawAdvertisementConfig = {
  adViewabilityTrackingEnable?: number;
  adViewabilityTrackingEventInterval?: number;
  adViewabilityTrackingQueueWorkerInterval?: number;
  adViewabilityTrackingTargetUrls?: string[];
  adViewabilityTrackingTestMode?: number;
  autoRefreshDesktopLimit?: number;
  autoRefreshInterval?: number;
  autoRefreshMinimalScrollAwayDurationMs?: number;
  autoRefreshMobileLimit?: number;
  directCampaignLineItemIds?: number[];
  disableAutoRefreshCountries?: string[];
  takeoverAd?: {
    style?: RawAdvertisementTakeoverAdSectionConfig;
  };
};

function parseConfig(raw?: RawAdvertisementConfig | null): AdvertisementConfig | undefined {
  if (!raw) return;

  const parsed: DeepPartial<AdvertisementConfig> = {
    autoRefresh: {
      desktopMaximumRefreshLimit: raw.autoRefreshDesktopLimit,
      disabledCountries: raw.disableAutoRefreshCountries,
      disabledDirectCampaignLineItemIds: raw.directCampaignLineItemIds,
      minimalScrollAwayDurationMs: raw.autoRefreshMinimalScrollAwayDurationMs,
      mobileMaximumRefreshLimit: raw.autoRefreshMobileLimit,
      refreshIntervalMs: raw?.autoRefreshInterval && raw.autoRefreshInterval * 1000,
    },
    takeoverAd: {
      style: {
        alwaysEnable: raw?.takeoverAd?.style?.alwaysEnable,
        country: raw.takeoverAd?.style?.country ?? [],
        enableInPageTypes: {
          article: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.article?.enable,
          },
          mainSection: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.mainSection?.enable,
          },
          subSection: {
            enable: !!raw?.takeoverAd?.style?.enableInPageTypes?.subSection?.enable,
          },
        },
        enableTakeoverByDateTime: raw.takeoverAd?.style?.enableTakeoverByDateTime,
        endDateTime: raw.takeoverAd?.style?.endDateTime,
        ipAddress: raw.takeoverAd?.style?.ipAddress,
        startDateTime: raw.takeoverAd?.style?.startDateTime,
        targetNonSubscribersOnly: raw.takeoverAd?.style?.targetNonSubscribersOnly,
        targetSubscribersOnly: raw.takeoverAd?.style?.targetSubscribersOnly,
      },
    },
    viewabilityTracking: {
      enable: raw.adViewabilityTrackingEnable === 1,
      eventIntervalMs: raw.adViewabilityTrackingEventInterval,
      testMode: {
        enable: raw.adViewabilityTrackingTestMode === 1,
        targetUrlPatterns: raw.adViewabilityTrackingTargetUrls,
      },
      workerIntervalMs: raw.adViewabilityTrackingQueueWorkerInterval,
    },
  };

  const merged = {
    autoRefresh: defaults(parsed.autoRefresh, defaultAdvertisementConfig.autoRefresh),
    takeoverAd: {
      style: defaults(parsed.takeoverAd?.style, defaultAdvertisementConfig.takeoverAd.style),
    },
    viewabilityTracking: defaults(
      parsed.viewabilityTracking,
      defaultAdvertisementConfig.viewabilityTracking,
    ),
  };

  return merged;
}
